import {
  DashboardDimensionItem,
  DashboardMetricItem,
} from 'domain/payload/response/clientTransmora/projectLayout/ProjectDashboardConfigResp';

export interface MetricKeySelection {
  metricId: string;
  metricDataId: string[];
}

export interface ChartWidget {
  id: string; // Internal unique ID for React keys
  chartName: string;
  chartType: 'bar' | 'pie' | 'heatmap' | 'line' | 'doughnut' | 'card' | string;
  colSpan?: number; // 1 to 6 (fixed 6-grid system per row)
  dimensionKey?: string[];
  metricKey?: MetricKeySelection[];
}

export interface DashboardGroup {
  id: string; // Internal unique ID for React keys
  group: ChartWidget[];
}

/**
 * Parses raw JSON string into DashboardGroup array safely.
 */
export const parseDashboardJson = (jsonStr: string): DashboardGroup[] => {
  if (!jsonStr || !jsonStr.trim()) return [];
  try {
    const rawArr = JSON.parse(jsonStr);
    if (!Array.isArray(rawArr)) return [];

    return rawArr.map((gObj: any, gIdx: number) => ({
      id: `group-${Date.now()}-${gIdx}`,
      group: Array.isArray(gObj.group)
        ? gObj.group.map((w: any, wIdx: number) => ({
            id: `widget-${Date.now()}-${gIdx}-${wIdx}`,
            chartName: w.chartName || 'Untitled Chart',
            chartType: w.chartType || 'bar',
            colSpan: typeof w.colSpan === 'number' ? Math.max(1, Math.min(6, w.colSpan)) : 3,
            dimensionKey: Array.isArray(w.dimensionKey) ? w.dimensionKey : undefined,
            metricKey: Array.isArray(w.metricKey)
              ? w.metricKey.map((mk: any) => ({
                  metricId: mk.metricId || '',
                  metricDataId: Array.isArray(mk.metricDataId) ? mk.metricDataId : [],
                }))
              : undefined,
          }))
        : [],
    }));
  } catch (err) {
    console.error('Failed to parse dashboard JSON:', err);
    return [];
  }
};

/**
 * Serializes DashboardGroup array into clean JSON string (stripping internal IDs).
 */
export const serializeDashboardGroups = (groups: DashboardGroup[]): string => {
  if (!groups || groups.length === 0) return '[]';

  const cleanArr = groups.map((g) => ({
    group: g.group.map((w) => {
      const widgetObj: any = {
        chartName: w.chartName,
        chartType: w.chartType,
      };

      if (w.colSpan && w.colSpan !== 3) {
        widgetObj.colSpan = w.colSpan;
      }

      if (w.dimensionKey && w.dimensionKey.length > 0) {
        widgetObj.dimensionKey = w.dimensionKey;
      }

      if (w.metricKey && w.metricKey.length > 0) {
        widgetObj.metricKey = w.metricKey.filter((mk) => mk.metricId);
      }

      return widgetObj;
    }),
  }));

  return JSON.stringify(cleanArr, null, 4);
};

/**
 * Returns default sample dashboard template structure matching real API response.
 */
export const getDefaultSampleDashboardGroups = (
  dimensions: DashboardDimensionItem[],
  metrics: DashboardMetricItem[]
): DashboardGroup[] => {
  const dim1 = dimensions.length > 0 ? dimensions[0].id : 'ed1eaffc-bde4-45b5-9f27-c436a2ad06af';
  const dim2 = dimensions.length > 1 ? dimensions[1].id : '9adcdcfb-34cf-4d8e-92c5-031e5520eefa';
  const dim3 = dimensions.length > 2 ? dimensions[2].id : 'c6469fff-2919-42f4-a709-115141cc54ad';
  const dim4 = dimensions.length > 3 ? dimensions[3].id : '26b01703-3b7f-43c5-adff-1658e35d13fe';

  const sampleMetricSelections: MetricKeySelection[] = metrics.map((met) => ({
    metricId: met.id,
    metricDataId: met.metricData ? met.metricData.map((md) => md.id) : [],
  }));

  return [
    {
      id: `group-sample-1`,
      group: [
        {
          id: `widget-sample-1`,
          chartName: 'Global Data',
          chartType: 'pie',
          colSpan: 3,
        },
        {
          id: `widget-sample-2`,
          chartName: 'Coverage Availability',
          chartType: 'heatmap',
          colSpan: 3,
          metricKey:
            sampleMetricSelections.length > 0
              ? sampleMetricSelections
              : [
                  {
                    metricId: '2b3c4d5e-6f7a-8b9c-0d1e-2f3a4b5c6d7e',
                    metricDataId: ['1ef7b4b7-efa8-4658-a91d-1fe51dfb3ce2'],
                  },
                ],
        },
      ],
    },
    {
      id: `group-sample-2`,
      group: [
        {
          id: `widget-sample-3`,
          chartName: 'By Area',
          chartType: 'bar',
          colSpan: 3,
          dimensionKey: [dim1],
        },
        {
          id: `widget-sample-4`,
          chartName: 'By Respondent Gender',
          chartType: 'bar',
          colSpan: 3,
          dimensionKey: [dim2],
        },
      ],
    },
    {
      id: `group-sample-3`,
      group: [
        {
          id: `widget-sample-5`,
          chartName: 'By Respondent Age',
          chartType: 'bar',
          colSpan: 3,
          dimensionKey: [dim3],
        },
        {
          id: `widget-sample-6`,
          chartName: 'By Top Buyer Profession',
          chartType: 'bar',
          colSpan: 3,
          dimensionKey: [dim4],
        },
      ],
    },
  ];
};
